import {
	Badge,
	Box,
	Flex,
	Icon,
	Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { format } from 'date-fns';
import { MdVpnKey, MdInfoOutline } from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { fetchMyApiKeys } from '../../store/features/apiKey/apiKey.slice';

function formatBRL(value?: number | null) {
	if (value === undefined || value === null) return null;
	return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function MinhasApiKeys() {
	const dispatch = useAppDispatch();
	const { myList, loadingMine } = useAppSelector((state: RootState) => state.apiKey);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const tileBg = useColorModeValue('secondaryGray.300', 'navy.900');

	useEffect(() => {
		dispatch(fetchMyApiKeys());
	}, [dispatch]);

	return (
		<Box pt='10px'>
			<Card>
				<Flex align='center' gap='10px' mb='6px'>
					<Icon as={MdVpnKey} color='brand.500' />
					<Text fontWeight='800' fontSize='lg' color={textColor}>
						Minhas API Keys
					</Text>
				</Flex>
				<Flex align='center' gap='6px' mb='16px'>
					<Icon as={MdInfoOutline} color={subColor} w='14px' h='14px' />
					<Text color={subColor} fontSize='sm'>
						Consulte suas chaves e o consumo. A criação de novas chaves é feita pelo administrador.
					</Text>
				</Flex>

				{loadingMine && myList.length === 0 && <Spinner color='brand.500' />}
				{!loadingMine && myList.length === 0 && (
					<Text color={subColor} fontSize='sm'>
						Você ainda não possui nenhuma API Key. Fale com o administrador para liberar o acesso via API.
					</Text>
				)}

				{myList.length > 0 && (
					<Box overflowX='auto'>
						<Table variant='simple' size='sm'>
							<Thead>
								<Tr>
									<Th>Nome</Th>
									<Th>Chave</Th>
									<Th isNumeric>Custo/consulta</Th>
									<Th isNumeric>Limite/h</Th>
									<Th isNumeric>Consumo 24h</Th>
									<Th isNumeric>Total consultas</Th>
									<Th>Última uso</Th>
									<Th>Status</Th>
								</Tr>
							</Thead>
							<Tbody>
								{myList.map(key => {
									const price = formatBRL(key.pricePerQuery);
									return (
										<Tr key={key.id}>
											<Td fontWeight='600' color={textColor}>
												{key.name}
											</Td>
											<Td>
												<Box
													as='span'
													bg={tileBg}
													borderRadius='8px'
													px='8px'
													py='2px'
													fontFamily='monospace'
													fontSize='xs'
													color={subColor}>
													{key.prefix ? `${key.prefix}••••••••` : '••••••••'}
												</Box>
											</Td>
											<Td isNumeric color={textColor}>
												{price ?? (
													<Text as='span' color={subColor} fontSize='xs'>
														padrão do usuário
													</Text>
												)}
											</Td>
											<Td isNumeric color={textColor}>
												{key.rateLimitPerHour ?? '—'}
											</Td>
											<Td isNumeric color={textColor}>
												{key.last24h ?? 0}
											</Td>
											<Td isNumeric color={textColor}>
												{key.totalQueries ?? 0}
											</Td>
											<Td color={subColor} fontSize='xs'>
												{key.lastUsedAt ? format(new Date(key.lastUsedAt), 'dd/MM/yyyy HH:mm') : 'nunca'}
											</Td>
											<Td>
												<Badge colorScheme={key.active ? 'green' : 'red'} borderRadius='8px'>
													{key.active ? 'Ativa' : 'Inativa'}
												</Badge>
											</Td>
										</Tr>
									);
								})}
							</Tbody>
						</Table>
					</Box>
				)}
			</Card>
		</Box>
	);
}
